import React, { useState, useContext } from 'react';
import MenuItem from '@material-ui/core/MenuItem';
import PhoneInput from 'react-phone-number-input';
import { useParams } from 'react-router-dom';
import metadata from 'libphonenumber-js/metadata.min.json';
import _ from 'lodash';
import Loading from '../../../components/Loading';
import AlertMessage from '../../../components/AlertMessage';
import PageNotFound from '../../../components/PageNotFound';
import { FlexFlow } from '../../../components/styles/Layout';
import {
  LPTitle,
  LPSubtitle,
  LPText,
  LPUrlRedirectWrapper,
  CardTextArea,
  BodyWrapper,
  LPLink,
} from '../../styles';
import { CssTextField, CssButton, PhoneInputWrapper } from '../../../components/Forms/styles';
import 'react-phone-number-input/style.css';

import { getTextValue } from '../../../helpers/data_helpers';
import PageLayout from '../../shared/PageLayout';
import BookingFooter from '../../BookingPage/content/BookingFooter';

import { PageContext } from '../context';

const PhoneCapturePageContent = () => {
  const { virtualPhoneNumber } = useParams();

  const context = useContext(PageContext);
  const {
    requestData: {
      queryLoading,
      queryError,
      queryData,
      mutationLoading,
      mutationError,
    },
    formData,
    setField,
    setFormData,
    saveData,
    updateUsedFields,
    hasErrorMessage,
    isFormValid,
  } = context;

  const [alertData, setAlertData] = useState({
    open: false,
    handleClose: () => false,
    variant: 'success',
    duration: 3000,
  });
  const [timeLeft, setTimeLeft] = useState(10);
  const [successfulSubmission, setSuccessfulSubmission] = useState(false);

  const handleCloseAlert = (data, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setAlertData({ ...data, open: false });
  };

  const showErrorMessage = (message) => {
    const newAlertData = {
      ...alertData,
      open: true,
      message,
      variant: 'error',
    };
    setAlertData({
      ...newAlertData,
      handleClose: (event, reason) => handleCloseAlert(newAlertData, reason),
    });
  };

  const handleOnBlur = (name) => {
    updateUsedFields(name);
  };

  const handleChange = (value, name) => {
    setField(name, value);
  };

  const clearForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      zipCode: '',
      questionId: '',
      phoneNumber: '',
    });
  };

  if (queryLoading) {
    return (
      <PageLayout isDark>
        <Loading />
      </PageLayout>
    );
  }

  if (queryError) {
    return (
      <PageLayout isDark>
        <BodyWrapper>
          <LPTitle> Oops! something went wrong: </LPTitle>
          <LPText>{queryError.message}</LPText>
        </BodyWrapper>
      </PageLayout>
    );
  }

  const capturePageData = queryData.optInForm;
  const isFormInValid = !isFormValid;

  if (_.isNull(capturePageData)) {
    return (
      <PageLayout isDark>
        <PageNotFound />
      </PageLayout>
    );
  }

  const {
    title,
    subtitle,
    agreementText,
    account: { avatarMedia, isVerified },
    afterSubmission,
    questions,
    instructionsText,
    coverMedia,
    targetUrlOnRedirect,
    redirectInstructions,
    waitingSecondsForRedirect,
  } = capturePageData;

  const splitName = title.split(' ');
  const avatarUrl = avatarMedia ? avatarMedia.uri : '';
  const hasURLRedirect = !!targetUrlOnRedirect;

  const handleURLRedirect = () => {
    let time = timeLeft;
    const interval = setInterval(() => {
      time -= 1;
      setTimeLeft(time);
      if (time <= 0) {
        clearInterval(interval);
        window.open(targetUrlOnRedirect);
      }
    }, 1000);
  };

  const handleSuccessfulSubmission = () => {
    setSuccessfulSubmission(true);

    if (hasURLRedirect) {
      setTimeLeft(waitingSecondsForRedirect);
      handleURLRedirect();
    }
    clearForm();
  };

  const {
    firstName,
    lastName,
    zipCode,
    questionId,
    primaryPhoneNumber,
  } = formData;

  const PageFooter = (
    <CssButton
      disabled={isFormInValid}
      className="pay_btn"
      onClick={() => saveData({
        onSuccess: (inputData, result) => {
          handleSuccessfulSubmission(inputData, result);
        },
        onError: (error) => {
          console.log(error);
          showErrorMessage(error.message);
        },
      })}
    >
      Submit
    </CssButton>
  );

  const account = {
    avatarImg: avatarUrl,
    firstName: splitName[0],
    lastName: splitName[1],
    description: subtitle,
    isVerified,
  };

  return (
    <PageLayout
      coverMedia={coverMedia}
      showFooter={!successfulSubmission}
      footer={PageFooter}
      isDark
      showUserInfo
      account={account}
    >
      <BodyWrapper>
        {successfulSubmission && !mutationLoading && !mutationError && (
          <>
            <LPSubtitle midFontSize centered>
              {getTextValue(afterSubmission)}
            </LPSubtitle>

            {hasURLRedirect && false && (
              <>
                <LPSubtitle centered>{getTextValue(redirectInstructions)}</LPSubtitle>
                <LPUrlRedirectWrapper>{targetUrlOnRedirect}</LPUrlRedirectWrapper>
                <LPSubtitle nomargin centered>
                  {`in ${timeLeft} Seconds`}
                </LPSubtitle>
              </>
            )}
          </>
        )}

        {!successfulSubmission && (
          <CardTextArea>
            <LPSubtitle centered>{getTextValue(instructionsText)}</LPSubtitle>
            <FlexFlow grow shrink column splitSpace="1em">
              <FlexFlow nowrap splitSpace="1em">
                <CssTextField
                  fullWidth
                  margin="dense"
                  variant="outlined"
                  label="First Name"
                  value={firstName}
                  onChange={(e) => handleChange(e.target.value, 'firstName')}
                  onBlur={() => handleOnBlur('firstName')}
                  error={hasErrorMessage('firstName').length > 0}
                  helperText={
                    hasErrorMessage('firstName').length > 0
                      ? hasErrorMessage('firstName').join(' | ')
                      : ''
                  }
                />
                <CssTextField
                  fullWidth
                  margin="dense"
                  variant="outlined"
                  label="Last Name"
                  value={lastName}
                  onChange={(e) => handleChange(e.target.value, 'lastName')}
                  onBlur={() => handleOnBlur('lastName')}
                  error={hasErrorMessage('lastName').length > 0}
                  helperText={
                    hasErrorMessage('lastName').length > 0
                      ? hasErrorMessage('lastName').join(' | ')
                      : ''
                  }
                />
              </FlexFlow>
              <CssTextField
                fullWidth
                type="number"
                margin="dense"
                variant="outlined"
                label="Zip/Postal code"
                value={zipCode}
                onChange={(e) => handleChange(e.target.value, 'zipCode')}
                onBlur={() => handleOnBlur('zipCode')}
                error={hasErrorMessage('zipCode').length > 0}
                helperText={
                  hasErrorMessage('zipCode').length > 0
                    ? hasErrorMessage('zipCode').join(' | ')
                    : ''
                }
              />
              <CssTextField
                select
                fullWidth
                margin="dense"
                variant="outlined"
                label="How should we connect?"
                value={questionId}
                onChange={(e) => handleChange(e.target.value, 'questionId')}
                onBlur={() => handleOnBlur('questionId')}
                error={hasErrorMessage('questionId').length > 0}
                helperText={
                  hasErrorMessage('questionId').length > 0
                    ? hasErrorMessage('questionId').join(' | ')
                    : ''
                }
              >
                {questions.map(({ id, question }) => (
                  <MenuItem key={id} value={id}>
                    {getTextValue(question)}
                  </MenuItem>
                ))}
              </CssTextField>

              <CardTextArea>
                <PhoneInputWrapper>
                  <PhoneInput
                    label="Phone"
                    metadata={metadata}
                    defaultCountry="US"
                    placeholder="Mobile # (w/country code)"
                    value={primaryPhoneNumber}
                    onChange={(value) => handleChange(value, 'primaryPhoneNumber')}
                    onBlur={() => handleOnBlur('primaryPhoneNumber')}
                  />
                </PhoneInputWrapper>
                {hasErrorMessage('primaryPhoneNumber').length > 0 && (
                  <LPText small error clear nomargin>
                    {hasErrorMessage('primaryPhoneNumber').join(' | ')}
                  </LPText>
                )}
              </CardTextArea>

              {mutationLoading && (
                <LPText
                  small
                  clear
                  nomargin
                  warning={isFormInValid || mutationError}
                  success={!isFormInValid && !mutationError}
                >
                  {mutationLoading && 'Loading...'}
                </LPText>
              )}

              <LPText
                small
                clear
                nomargin
                warning={isFormInValid || mutationError}
                success={!isFormInValid && !mutationError}
              >
                {isFormInValid || mutationError
                  ? '* All fields are required'
                  : 'Looks good! Hit Submit'}
              </LPText>

              <LPText small centered>
                {getTextValue(agreementText)}
              </LPText>
            </FlexFlow>
          </CardTextArea>
        )}
        <AlertMessage {...alertData} />
      </BodyWrapper>

      {successfulSubmission && !mutationLoading && !mutationError && (
        <LPLink bottom to={`/s/${virtualPhoneNumber}`}>
          <BookingFooter footerHeading="All Set!" footerParagraph="Tab here to view my links." />
        </LPLink>
      )}
    </PageLayout>
  );
};

export default PhoneCapturePageContent;
